/** @fileoverview Date, time and date-time field of an app form: the app's own field with a calendar and a time list in a popup. */

import { useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { CalendarDays, Clock } from 'lucide-react';
import { cn } from 'cn';

import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

import { formatDate } from './helpers';

/** Minutes between two times in the time list when the input sets no step. */
const DEFAULT_MINUTE_STEP = 15;
/** Time a date-time gets when the viewer picks the day first. */
const DEFAULT_TIME = '09:00';
/** First year the year menu offers, for dates of birth. */
const FIRST_YEAR = 1920;
/** Years after this one that the year menu offers. */
const YEARS_AHEAD = 10;

/* Calendar parts in the app's steps: day cells as tall as a compact control. */
const CALENDAR_CLASSES = {
  root: 'relative p-1',
  months: 'flex flex-col',
  month: 'flex flex-col gap-2',
  month_caption: 'flex h-app-control-compact items-center justify-center px-8',
  caption_label: 'flex items-center gap-1 type-body font-medium',
  dropdowns: 'flex items-center gap-1',
  dropdown_root: 'relative rounded-md px-1 hover:bg-muted',
  dropdown: 'absolute inset-0 cursor-pointer opacity-0',
  nav: 'absolute inset-x-1 top-1 flex items-center justify-between',
  button_previous:
    'inline-flex size-app-control-compact items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
  button_next:
    'inline-flex size-app-control-compact items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
  chevron: 'size-4 fill-current',
  weekdays: 'flex',
  weekday: 'size-app-control-compact type-caption font-normal text-muted-foreground',
  week: 'mt-0.5 flex',
  day: 'size-app-control-compact p-0 text-center',
  day_button:
    'size-app-control-compact rounded-md type-body transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
  selected: '*:bg-primary *:font-medium *:text-primary-foreground *:hover:bg-primary',
  today: '*:font-semibold *:text-primary',
  outside: 'text-muted-foreground opacity-50',
  disabled: 'pointer-events-none opacity-40',
  hidden: 'invisible',
};

type DateTimeFieldProps = {
  controlId: string;
  inputType: 'date' | 'time' | 'datetime';
  /** `YYYY-MM-DD`, `HH:MM` or `YYYY-MM-DDTHH:MM`; empty when nothing is picked. */
  value: string;
  onChange: (value: string | undefined) => void;
  /** Called when the popup closes, as the field's blur. */
  onCommit?: () => void;
  required?: boolean;
  describedBy?: string;
  invalid: boolean;
  placeholder?: string;
  /** Earliest and latest value, in the same form as the value. */
  min?: string;
  max?: string;
  /** Seconds between two times; times step by 15 minutes when left out. */
  step?: number;
};

/**
 * Turns a calendar day into `YYYY-MM-DD`, from its local date parts.
 * @param date the picked day
 */
const toIsoDay = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

/**
 * Reads `YYYY-MM-DD` as a local calendar day, or nothing when the text is not a day.
 * @param day the day text, alone or at the start of a date-time
 */
const parseIsoDay = (day?: string): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day ?? '');
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : undefined;
};

/**
 * Picks the month a calendar opens on when nothing is picked: today, kept inside the limits.
 * @param earliest the first day allowed
 * @param latest the last day allowed
 */
const openingDay = (earliest?: Date, latest?: Date): Date => {
  const today = new Date();
  if (earliest && today < earliest) {
    return earliest;
  }
  return latest && today > latest ? latest : today;
};

/**
 * Lists the times of one day, from 00:00, every step.
 * @param stepSeconds seconds between two times, the input's step
 */
const listTimes = (stepSeconds?: number): string[] => {
  const stepMinutes =
    stepSeconds && stepSeconds >= 60 ? Math.round(stepSeconds / 60) : DEFAULT_MINUTE_STEP;
  return Array.from({ length: Math.ceil((24 * 60) / stepMinutes) }, (_, timeIndex) => {
    const minutes = timeIndex * stepMinutes;
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(
      2,
      '0'
    )}`;
  });
};

/**
 * Builds the text the closed field shows: the day as lists show dates, and the time.
 * @param inputType the kind of field
 * @param value the field value
 */
const displayTextOf = (inputType: DateTimeFieldProps['inputType'], value: string): string => {
  if (!value) {
    return '';
  }
  if (inputType === 'time') {
    return value.slice(0, 5);
  }
  const [day, time] = value.split('T');
  const dayText = formatDate(day) || day;
  return inputType === 'datetime' && time ? `${dayText}, ${time.slice(0, 5)}` : dayText;
};

/**
 * Shows a date, time or date-time as the app's own field. A click opens a popup with a month
 * calendar, a list of times, or both side by side. A hidden input carries the value and
 * `required`, so the form checks it like any other field.
 * @param props the field id, kind, value, change and commit handlers, limits and look
 */
export default function DateTimeField({
  controlId,
  inputType,
  value,
  onChange,
  onCommit,
  required,
  describedBy,
  invalid,
  placeholder,
  min,
  max,
  step,
}: DateTimeFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const timeListRef = useRef<HTMLDivElement>(null);
  const [day, time = ''] =
    inputType === 'time' ? ['', value] : (value.split('T') as [string, string?]);
  const showsCalendar = inputType !== 'time';
  const showsTimes = inputType !== 'date';
  const Icon = inputType === 'time' ? Clock : CalendarDays;
  const displayText = displayTextOf(inputType, value);

  /* Times outside the limits are left out; for a date-time only on the first and last day. */
  const [minDay, minTime = ''] = inputType === 'time' ? ['', min ?? ''] : (min ?? '').split('T');
  const [maxDay, maxTime = ''] = inputType === 'time' ? ['', max ?? ''] : (max ?? '').split('T');
  const times = listTimes(step).filter(
    (candidate) =>
      !(minTime && (inputType === 'time' || day === minDay) && candidate < minTime.slice(0, 5)) &&
      !(maxTime && (inputType === 'time' || day === maxDay) && candidate > maxTime.slice(0, 5))
  );
  const thisYear = new Date().getFullYear();

  /* The list opens at the picked time, or at the morning, instead of at midnight. */
  useEffect(() => {
    if (!isOpen || !showsTimes) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const list = timeListRef.current;
      const target =
        list?.querySelector<HTMLElement>('[data-picked]') ??
        list?.querySelector<HTMLElement>(`[data-time="${DEFAULT_TIME}"]`);
      target?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, showsTimes]);

  /**
   * Opens or closes the popup, and commits the value when it closes.
   * @param open whether the popup opens
   */
  const changeOpen = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      onCommit?.();
    }
  };

  /**
   * Picks a day: a date closes the popup, a date-time keeps it open for the time.
   * @param picked the day, or nothing when the viewer clicks the picked day again
   */
  const pickDay = (picked?: Date) => {
    if (!picked) {
      return;
    }
    const pickedDay = toIsoDay(picked);
    if (inputType === 'date') {
      onChange(pickedDay);
      changeOpen(false);
      return;
    }
    onChange(`${pickedDay}T${time || DEFAULT_TIME}`);
  };

  /**
   * Picks a time and closes the popup; a date-time without a day keeps it open for the day.
   * @param pickedTime the time, `HH:MM`
   */
  const pickTime = (pickedTime: string) => {
    if (inputType === 'time') {
      onChange(pickedTime);
      changeOpen(false);
      return;
    }
    if (day) {
      onChange(`${day}T${pickedTime}`);
      changeOpen(false);
    }
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={changeOpen}>
        <PopoverTrigger
          id={controlId}
          type="button"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-haspopup="dialog"
          className="group flex h-app-control w-full items-center gap-2 rounded-lg border border-input bg-card px-2.5 text-left type-body transition-colors outline-none hover:border-ring/60 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:bg-card"
        >
          <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className={cn('flex-1 truncate', !displayText && 'text-muted-foreground')}>
            {displayText ||
              placeholder ||
              (inputType === 'time'
                ? 'Pick a time'
                : inputType === 'date'
                ? 'Pick a date'
                : 'Pick a date and time')}
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto gap-0 p-1 duration-150 ease-out">
          <div className="flex gap-1">
            {showsCalendar && (
              <DayPicker
                mode="single"
                required={false}
                selected={parseIsoDay(day)}
                defaultMonth={
                  parseIsoDay(day) ?? openingDay(parseIsoDay(minDay), parseIsoDay(maxDay))
                }
                onSelect={pickDay}
                weekStartsOn={1}
                showOutsideDays
                captionLayout="dropdown"
                startMonth={new Date(FIRST_YEAR, 0)}
                endMonth={new Date(thisYear + YEARS_AHEAD, 11)}
                disabled={[
                  ...(parseIsoDay(minDay) ? [{ before: parseIsoDay(minDay) as Date }] : []),
                  ...(parseIsoDay(maxDay) ? [{ after: parseIsoDay(maxDay) as Date }] : []),
                ]}
                classNames={CALENDAR_CLASSES}
              />
            )}
            {showsTimes && (
              <div
                ref={timeListRef}
                role="listbox"
                aria-label="Times"
                className={cn(
                  'flex max-h-72 w-24 flex-col gap-0.5 overflow-y-auto p-1',
                  showsCalendar && 'border-l border-border'
                )}
              >
                {times.map((candidate) => (
                  <Button
                    key={candidate}
                    type="button"
                    variant="ghost"
                    role="option"
                    aria-selected={candidate === time.slice(0, 5)}
                    data-time={candidate}
                    data-picked={candidate === time.slice(0, 5) || undefined}
                    disabled={inputType === 'datetime' && !day}
                    className="h-app-control-compact w-full shrink-0 justify-center type-body font-normal tabular-nums data-picked:bg-primary data-picked:text-primary-foreground"
                    onClick={() => pickTime(candidate)}
                  >
                    {candidate}
                  </Button>
                ))}
              </div>
            )}
          </div>
          {value && !required && (
            <div className="flex justify-end border-t border-border px-1 pt-1">
              <Button
                type="button"
                variant="ghost"
                className="h-app-control-compact type-small text-muted-foreground"
                onClick={() => {
                  onChange(undefined);
                  changeOpen(false);
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      <input
        aria-hidden="true"
        tabIndex={-1}
        name={controlId}
        required={required}
        value={value}
        onChange={() => undefined}
        className="sr-only"
      />
    </div>
  );
}
